import { formatServicePeriod } from '~support-at-home/lib/derive';

import { Column, Row } from '~components/table-view';
import { multiSelectEditor } from '~components/table-view/columnEditors';
import { genericCellRender } from '~components/table-view/columnRenderers';
import AssociatedServices from '~support-at-home/components/config/service-associations/AssociatedServices';
import SahServiceCell from '~support-at-home/components/config/service-associations/SahServiceCell';

import { ServiceTypeRow } from '~appointments/reducers/serviceTypes';

// Every column but Associated Services is fixed, so that one absorbs the remaining width and is
// the only thing that reflows as the window changes. Widths are sized to the longest real value
// each column holds rather than to its header: Category tops out at "Everyday Living"; the service
// names run past 60 characters ("Aboriginal or Torres Strait Islander Health Practitioner
// assistance") and wrap onto a second line at this width rather than pushing everything else over;
// Period is sized to a full two-date range, "01/Mar/2025 - 30/Sep/2026".
const CATEGORY_WIDTH = '150px';
const SERVICE_WIDTH = '360px';
// Wide enough for a full two-date range plus cell padding, so a period never wraps onto a second
// line - the column is only useful if the whole range can be read at a glance.
const PERIOD_WIDTH = '230px';

const columnsFactory = (allServiceTypes: ServiceTypeRow[], eligibleServiceTypes: Set<number>) => {
  const servicesColumn: Column = {
    id: 'service_type_ids',
    header: 'Associated Services',
    caption: 'The TurnPoint service types mapped to this Support at Home service',
    type: 'multi-lookup',
    // Deliberately no width - the only fluid column.
    classNames: { cell: 'associated-services-cell' },
    options: allServiceTypes.map(({ id, name }) => ({ id, name })),
    renderReadOnly: (_value, row) => AssociatedServices({ row, serviceTypes: allServiceTypes }),
  };

  servicesColumn.renderEditor = multiSelectEditor(
    (row: any) => {
      const selected = (row?.service_type_ids ?? []) as number[];
      // Blocked for THIS occurrence only. The same service type stays on offer against a service
      // covering a different period, which is what lets an association follow a service across a
      // contribution-category cutover without being re-entered.
      const unavailable = new Set((row?.unavailable_service_type_ids ?? []) as number[]);

      const available = allServiceTypes
        .map(({ id, name }) => ({ id, name }))
        .filter(({ id }) => selected.includes(id) || (eligibleServiceTypes.has(id) && !unavailable.has(id)));

      // allServiceTypes only loads non-deleted service types, but an existing association can
      // still point at one that was deleted afterwards. multiSelectEditor only restores a missing
      // selected value against a static options array - this column passes a resolver function
      // instead, so without this fallback a deleted id would silently vanish from the select's
      // value and be dropped from service_type_ids on the next save of this row, regardless of
      // what was actually being edited.
      const missing = selected
        .filter((id) => !available.some((option) => option.id === id))
        .map((id) => ({ id, name: `Service #${id}` }));

      return available.concat(missing);
    },
    true,
    servicesColumn,
  );

  const columns: Column[] = [
    {
      id: 'category',
      header: 'Category',
      caption: 'The participant contribution category the service falls under',
      type: 'text',
      style: { width: CATEGORY_WIDTH },
      isEditable: false,
    },
    {
      id: 'name',
      header: 'SAH Service',
      caption: 'The Support at Home service published by Services Australia',
      type: 'text',
      style: { width: SERVICE_WIDTH },
      isEditable: false,
      renderReadOnly: (_value, row: Row) => SahServiceCell({ row }),
    },
    // Start and end as one range. A service that exists over two periods appears twice under the
    // same name, so this column is what tells the occurrences apart - which reads better as a
    // single fact than as two columns a user has to mentally join.
    {
      id: 'service_period',
      header: 'Period',
      caption: 'The date range this Services Australia service is valid for',
      type: 'text',
      style: { width: PERIOD_WIDTH },
      classNames: { cell: 'sah-service-period' },
      isEditable: false,
      renderReadOnly: genericCellRender((_value, row) => formatServicePeriod(row?.start_date, row?.end_date)),
    },
    servicesColumn,
  ];

  return columns;
};

export default columnsFactory;
